import { Component, OnDestroy, OnInit } from "@angular/core";
import { Store } from "@ngrx/store";
import { combineLatest, Observable } from "rxjs";
import {
  catchError,
  filter,
  finalize,
  first,
  map,
  mergeMap,
  startWith,
  switchMap,
  tap
} from "rxjs/operators";
import { AbstractAuthorizationService } from "src/app/authorization/services/abstractAuthorization.service";
import { DialogResult } from "src/app/common/dialog/dialog.component";
import {
  Circulation,
  Document,
  EntityId,
  IDocument,
  IHyperlinkToSave,
  IPermissionExtendedStep,
  IStepInit,
  IUser,
  Step
} from "src/app/interfaces";
import { LogClass, Logger } from "src/app/logger";
import { NotificationLevel } from "src/app/notifications/notification";
import { NotificationFacade } from "src/app/notifications/services/notification.facade";
import { NotificationActions } from "src/app/notifications/state/actions";
import { NavigationService } from "src/app/services";
import { CirculationFacade } from "src/app/services/circulation.facade";
import { DocumentFacade } from "src/app/services/document.facade";
import { StepFacade } from "src/app/step/services/step.facade";
import { UserFacade } from "src/app/user/services/user.facade";
import { selectCurrentUser } from "src/app/user/state";
import { SubSink } from "subsink";
import { CirculationPermissions } from "../models";

@LogClass()
@Component({
  selector: "circ-circulation-details",
  templateUrl: "./circulation-details.component.html",
  styleUrls: ["./circulation-details.component.scss"],
  standalone: false
})
export class CirculationDetailsComponent implements OnInit, OnDestroy {
  public circulation$: Observable<Circulation>; //circ-information
  public permissions$: Observable<CirculationPermissions>; //circ-information

  public additionalReportRecipients = 0;

  public canAddStep = false;
  public hasSteps = false;
  public circulation: Circulation = null;
  public document$: Observable<Document[]>;
  public hasDownloadableDocs$: Observable<boolean>;
  public steps: Observable<IPermissionExtendedStep[]>;
  public toggleDetails = false;
  public downloadInProgress = false;

  private loadingCirc = true;
  private loadingSteps = true;
  private logger: Logger;
  private subscriptions = new SubSink();
  private currentStep: Observable<Step | null>;

  constructor(
    private store: Store,
    private navigate: NavigationService,
    private circulationFacade: CirculationFacade,
    private documentFacade: DocumentFacade,
    private stepFacade: StepFacade,
    private authorizationService: AbstractAuthorizationService,
    private userFacade: UserFacade,
    private notificationFacade: NotificationFacade
  ) {
    this.logger.debug("ctor");
  }

  public cancelDialogOpened = false;

  public addStep(): void {
    this.navigate.toStepCreation(this.circulation.id);
  }

  public onCancelDialogClosed(result: DialogResult): void {
    if (result !== DialogResult.Yes) {
      this.notificationFacade.info("circulation.cancel.dialog.notification.no");
      return;
    }
    this.circulationFacade
      .cancel(this.circulation)
      .pipe(first())
      .subscribe({
        next: () => {
          this.notificationFacade.success(
            "circulation.cancel.dialog.notification.yes"
          );
          this.navigate.toDashboard();
        },
        error: () =>
          this.notificationFacade.showError(
            "circulation.cancel.dialog.notification.error"
          )
      });
  }

  public cancelCirculation(): void {
    this.cancelDialogOpened = true;
  }

  public cellClickHandler({ dataItem }: { dataItem: IStepInit }): void {
    this.navigate.toStep(dataItem.circulationId, dataItem.id);
  }

  public editCirculation(): void {
    this.navigate.toCirculationEdit(this.circulation.id);
  }

  public isLoading(): boolean {
    return this.loadingSteps || this.loadingCirc;
  }
  public isReady(): boolean {
    return !this.isLoading();
  }
  public copyCirculation(): void {
    this.loadingCirc = true;
    this.circulationFacade
      .copyCirculation(this.circulation.id)
      .pipe(
        filter((x) => !!x),
        first()
      )
      .subscribe({
        next: (id) => {
          this.loadingCirc = false;
          this.loadCirculation(id.id);
          this.navigate.toCirculation(id.id);
        },
        error: (err) => {
          this.loadingCirc = false;
          this.navigate.toError(
            "Error in Circulation copy. - " + JSON.stringify(err)
          );
        }
      });
  }

  public ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  public ngOnInit(): void {
    this.logger.info("init");

    this.circulation$ = this.circulationFacade.getCurrentCirculation();
    this.subscriptions.sink = this.circulation$.subscribe((c) =>
      this.loadCirculation(c.id)
    );
  }

  private allStepsContainUser(steps: Step[]): boolean {
    for (let idx = 0; idx < steps.length; idx++) {
      const step = steps[idx];
      if (step.props.tasks.assignees.length === 0) {
        return false;
      }
      for (
        let userIdx = 0;
        userIdx < step.props.tasks.assignees.length;
        userIdx++
      ) {
        if (!step.props.tasks.assignees[userIdx].user) {
          return false;
        }
      }
    }
    return true;
  }

  private loadCirculation(id: EntityId): void {
    this.logger.info("init");
    this.loadingCirc = true;
    this.circulation$ = this.circulationFacade.getCirculation(id);
    this.currentStep = this.circulation$.pipe(
      switchMap((c) => this.stepFacade.getStepsForCirculation(c.id)),
      map((s) => s.filter((x) => x.props.status == "InProgress")[0] || null)
    );

    this.circulation$
      .pipe(
        filter((c) => !!c),
        first()
      )
      .subscribe((c) => {
        this.loadingCirc = false;
        this.circulation = c;
      });
    const docs = this.documentFacade.getForCurrentCirculation();
    this.document$ = combineLatest([
      docs,
      this.circulation$,
      this.currentStep
    ]).pipe(
      map(([documents, circ, step]) => {
        return documents
          .sort((a, b) => {
            if (typeof a.name === "undefined" || a.name == null) {
              if (typeof b.name === "undefined" || a.name == null) {
                return 0;
              }
              return 1;
            }

            try {
              return a.name.localeCompare(b.name);
            } catch {
              return -1;
            }
          })
          .map(
            (d) =>
              new Document(
                d,
                circ,
                step,
                this.userFacade,
                this.authorizationService
              )
          );
      }),
      startWith([])
    );

    this.hasDownloadableDocs$ = this.document$.pipe(
      map((docs) => {
        return docs.filter((d) => !d.doc.isHyperlink);
      }),
      map((docs) => docs.length > 0)
    );

    const id$ = this.circulation$.pipe(map((c) => c.id));

    const currentUser$ = this.store
      .select(selectCurrentUser)
      .pipe(this.logger.observableOperator("Current user"));

    this.permissions$ = combineLatest([
      currentUser$,
      this.circulation$,
      this.currentStep
    ]).pipe(
      switchMap(([user, circ, step]) => {
        const canAddDocuments = step
          ? step.canAddDocuments(
            user,
            this.authorizationService.isAdmin(user.roles)
          )
          : circ.canStart(user, this.authorizationService.isAdmin(user.roles));

        return combineLatest([
          circ.canStart(user, this.authorizationService.isAdmin(user.roles)),
          circ.canCancel(user, this.authorizationService.isAdmin(user.roles)),
          circ.canEdit(user, this.authorizationService.isAdmin(user.roles)),
          circ.canCopy(user, this.authorizationService.isAdmin(user.roles)),
          canAddDocuments
        ]);
      }),
      map(([canStart, canCancel, canEdit, canCopy, canAddDocuments]) => {
        return {
          canAddDocuments,
          canStart,
          canCancel,
          canEdit,
          canCopy
        };
      }),
      this.logger.observableOperator("Permissions")
    );

    this.subscriptions.sink = this.permissions$.subscribe(
      (p) => {
        this.canAddStep = p.canEdit;
      },
      (err) => {
        this.logger.error("loading circ", err);
        throw err;
      }
    );

    this.steps = id$.pipe(
      switchMap((id) =>
        combineLatest([
          currentUser$,
          this.stepFacade.getStepsForCirculation(id)
        ])
      ),
      tap(
        ([, steps]) =>
        (this.hasSteps =
          steps && steps.length > 0 && this.allStepsContainUser(steps))
      ),
      map(([user, steps]) => {
        const s = steps.map((step) => ({
          canEditStep: false,
          canWorkStep: false,
          canOverrideStep: false,
          ...step.props
        }));
        return [user, s] as [IUser, typeof s];
      }),
      map(([user, steps]) => {
        return steps.map((step) => {
          Step.canEdit(
            step,
            user,
            this.circulationFacade,
            this.authorizationService.isAdmin(user.roles)
          )
            .pipe(first())
            .subscribe((b) => {
              step.canEditStep = b;
            });
          Step.canWork(step, user)
            .pipe(first())
            .subscribe((b) => {
              step.canWorkStep = b;
            });
          Step.canOverride(
            step,
            user,
            this.circulationFacade,
            this.authorizationService.isAdmin(user.roles)
          )
            .pipe(first())
            .subscribe((b) => {
              step.canOverrideStep = b;
            });
          //step.tasks.assignees.sort((a) => this.sortAssignees(a)); //Is readonly -> solve later/ Just make it run
          return step;
        });
      }),
      map((steps) => steps.sort((a, b) => a.stepNo - b.stepNo)),
      this.logger.observableOperator("Steps"),
      catchError((err) => {
        this.logger.error("loading steps", err);
        throw err;
      })
    );

    this.steps.pipe(first()).subscribe({
      next: () => (this.loadingSteps = false),
      error: () => this.navigate.toError("error.generic")
    });
    // endpoints
  }

  public onAddHyperlink(doc: IHyperlinkToSave): void {
    this.currentStep
      .pipe(
        first(),
        tap(() => this.documentFacade.uploadStarted()),
        mergeMap((s) => {
          return this.documentFacade.addHyperlink(
            doc,
            this.circulation.id,
            s?.props.stepNo || 0
          );
        }),
        finalize(() => this.documentFacade.uploadCompleted())
      )
      .subscribe({
        error: (err) => {
          this.notificationFacade.showError(
            "Error while saving the hyperlink!" + err.error.message
          );
        },
        next: (d) => {
          this.logger.info("saved hyperlink: " + d.id);
        }
      });
  }

  public onDeleteDocument(doc: IDocument): void {
    this.documentFacade.remove(this.circulation.id, doc.id);
  }

  public onDownloadDocument(doc: IDocument): void {
    this.downloadInProgress = true;
    this.documentFacade
      .download(this.circulation.id, doc)
      .pipe(first())
      .subscribe({
        error: (err) => {
          this.notificationFacade.showError(
            "Error while downloading the document!" + err.error.message
          );
          this.downloadInProgress = false;
        },
        next: (num) => {
          this.logger.info(`saved ${num} documents`);
          this.downloadInProgress = false;
        }
      });
  }

  public onDownloadAllDocuments(): void {
    this.downloadInProgress = true;
    this.documentFacade
      .downloadAll(this.circulation.id)
      .pipe(first())
      .subscribe({
        error: (err) => {
          this.notificationFacade.showError(
            "Error while downloading the document!" + err.error.message
          );
          this.downloadInProgress = false;
        },
        next: (num) => {
          this.logger.info(`saved ${num} documents`);
          this.downloadInProgress = false;
        }
      });
  }

  public onOpenDocument(doc: IDocument): void {
    this.documentFacade.open(this.circulation.id, doc);
  }

  public onEdit(step: IStepInit): void {
    this.navigate.toStepEdit(step.circulationId, step.id);
  }

  public onForward(step: IStepInit): void {
    this.navigate.toStepForward(step.circulationId, step.id);
  }

  public onOverride(step: IStepInit): void {
    this.navigate.toStepOverride(step.circulationId, step.id);
  }

  public onWork(step: IStepInit): void {
    this.navigate.toStepWork(step.circulationId, step.id);
  }

  public reorderSteps(): void {
    this.navigate.toStepReorder(this.circulation.id);
  }

  public startCirculation(): void {
    this.loadingCirc = true;
    this.subscriptions.sink = this.circulationFacade
      .start(this.circulation)
      .subscribe({
        error: (err) => {
          this.loadingCirc = false;
          this.notificationFacade.showError(
            "Error while starting the circulation. Please ask your Administrator for help.!" +
            err.error.message
          );
          this.downloadInProgress = false;
        },
        next: () => {
          const notification = {
            content: "NOTIFICATION_CIRCULATION_STARTED",
            level: "success" as NotificationLevel
          };
          this.store.dispatch(
            NotificationActions.showNotification({ notification })
          );
          this.navigate.toDashboard();
        }
      });
  }
}
